/**
 * React Query хук для работы с кластерами услуг
 * Дата: 07.02.2026 - Создан для PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3)
 * Связан с: frontend/src/api/serviceClusters.ts, frontend/src/pages/Services.tsx
 * 
 * КАК РАБОТАЕТ:
 * - Загрузка списка кластеров через React Query
 * - CRUD операции для кластеров
 * - Управление услугами в кластере
 * - Привязка шаблонов к кластерам
 * 
 * КОМПОЗИЦИЯ ФУНКЦИЙ:
 *   useServiceClusters() → serviceClustersApi.list()
 *   createCluster() → serviceClustersApi.create()
 *   addService() → serviceClustersApi.addService()
 *   linkTemplate() → serviceClustersApi.linkTemplate()
 * 
 * ИСПОЛЬЗОВАНИЕ ИЗ ДРУГИХ МОДУЛЕЙ:
 * - pages/Services.tsx: использует для управления кластерами на странице услуг
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  serviceClustersApi,
  CreateClusterRequest,
  UpdateClusterRequest,
  AddServiceRequest,
  ReorderServicesRequest,
  LinkTemplateRequest,
  PreviewPresetsRequest,
} from '@/api/serviceClusters';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from './useToast';

export function useServiceClusters(includeInactive = false) {
  const { tenantId } = useTenantScope();

  const clustersQuery = useQuery({
    queryKey: ['service-clusters', tenantId, includeInactive],
    queryFn: () => serviceClustersApi.list(includeInactive, tenantId),
    enabled: !!tenantId,
    staleTime: 30_000,
  });

  return {
    clusters: clustersQuery.data?.clusters || [],
    isLoading: clustersQuery.isLoading,
    isFetching: clustersQuery.isFetching,
    isError: clustersQuery.isError,
    error: clustersQuery.error,
    refetch: clustersQuery.refetch,
  };
}

export function useServiceCluster(id: string) {
  const { tenantId } = useTenantScope();

  return useQuery({
    queryKey: ['service-cluster', id, tenantId],
    queryFn: () => serviceClustersApi.get(id, tenantId),
    enabled: !!id && !!tenantId,
  });
}

export function useClusterServices(clusterId: string) {
  const { tenantId } = useTenantScope();

  return useQuery({
    queryKey: ['cluster-services', clusterId, tenantId],
    queryFn: () => serviceClustersApi.getServices(clusterId, tenantId),
    enabled: !!clusterId && !!tenantId,
  });
}

export function useClusterTemplates(clusterId: string) {
  const { tenantId } = useTenantScope();

  return useQuery({
    queryKey: ['cluster-templates', clusterId, tenantId],
    queryFn: () => serviceClustersApi.getTemplates(clusterId, tenantId),
    enabled: !!clusterId && !!tenantId,
  });
}

export function useClusterTriggerBindings(clusterId: string) {
  const { tenantId } = useTenantScope();

  return useQuery({
    queryKey: ['cluster-trigger-bindings', clusterId, tenantId],
    queryFn: () => serviceClustersApi.listTriggerBindings(clusterId, tenantId),
    enabled: !!clusterId && !!tenantId,
  });
}

export function useServiceClusterPresets() {
  const { tenantId } = useTenantScope();

  return useQuery({
    queryKey: ['service-cluster-presets', tenantId],
    queryFn: () => serviceClustersApi.listPresets(tenantId),
    enabled: !!tenantId,
  });
}

export function useCreateCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: (data: CreateClusterRequest) => serviceClustersApi.create(data, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      showSuccess('Кластер успешно создан');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при создании кластера');
    },
  });
}

export function useUpdateCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClusterRequest }) =>
      serviceClustersApi.update(id, data, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['service-cluster'] });
      showSuccess('Кластер успешно обновлён');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при обновлении кластера');
    },
  });
}

export function useDeleteCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: (id: string) => serviceClustersApi.delete(id, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      showSuccess('Кластер успешно удалён');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при удалении кластера');
    },
  });
}

export function useAddServiceToCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, data }: { clusterId: string; data: AddServiceRequest }) =>
      serviceClustersApi.addService(clusterId, data, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['cluster-services', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['medical-services'] });
      showSuccess('Услуга добавлена в кластер');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при добавлении услуги в кластер');
    },
  });
}

export function useRemoveServiceFromCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, serviceId }: { clusterId: string; serviceId: string }) =>
      serviceClustersApi.removeService(clusterId, serviceId, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['cluster-services', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['medical-services'] });
      showSuccess('Услуга удалена из кластера');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при удалении услуги из кластера');
    },
  });
}

export function useReorderClusterServices() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, data }: { clusterId: string; data: ReorderServicesRequest }) =>
      serviceClustersApi.reorderServices(clusterId, data, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cluster-services', variables.clusterId] });
      showSuccess('Порядок услуг обновлён');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при изменении порядка услуг');
    },
  });
}

export function useLinkTemplateToCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, data }: { clusterId: string; data: LinkTemplateRequest }) =>
      serviceClustersApi.linkTemplate(clusterId, data, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cluster-templates', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      showSuccess('Шаблон привязан к кластеру');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при привязке шаблона к кластеру');
    },
  });
}

export function useUnlinkTemplateFromCluster() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, templateId, triggerType }: { clusterId: string; templateId: string; triggerType: 'after_completed' | 'after_cancelled' | 'manual' }) =>
      serviceClustersApi.unlinkTemplate(clusterId, templateId, triggerType, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cluster-templates', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      showSuccess('Шаблон отвязан от кластера');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при отвязке шаблона от кластера');
    },
  });
}

export function useSetClusterTriggerBinding() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({
      clusterId,
      triggerType,
      templateId,
      channel,
      delayHours,
      dedupScope,
    }: {
      clusterId: string;
      triggerType: 'after_completed' | 'after_cancelled' | 'manual';
      templateId: string;
      channel?: string;
      delayHours?: number;
      dedupScope?: string;
    }) =>
      serviceClustersApi.setTriggerBinding(clusterId, triggerType, templateId, channel, tenantId, {
        delayHours,
        dedupScope,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cluster-trigger-bindings', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['cluster-templates', variables.clusterId] });
      showSuccess('Привязка cluster-trigger сохранена');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при сохранении cluster-trigger привязки');
    },
  });
}

export function useClearClusterTriggerBinding() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({
      clusterId,
      triggerType,
      channel,
    }: {
      clusterId: string;
      triggerType: 'after_completed' | 'after_cancelled' | 'manual';
      channel?: string;
    }) => serviceClustersApi.clearTriggerBinding(clusterId, triggerType, channel, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cluster-trigger-bindings', variables.clusterId] });
      queryClient.invalidateQueries({ queryKey: ['cluster-templates', variables.clusterId] });
      showSuccess('Cluster-trigger привязка очищена');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при очистке cluster-trigger привязки');
    },
  });
}

export function useManualTriggerCluster() {
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: ({ clusterId, appointmentId }: { clusterId: string; appointmentId: string }) =>
      serviceClustersApi.manualTrigger(clusterId, appointmentId, tenantId),
    onSuccess: () => {
      showSuccess('Manual cluster follow-up поставлен в очередь');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при ручном запуске cluster follow-up');
    },
  });
}

export function usePreviewServiceClusterPresets() {
  const { showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: (data: PreviewPresetsRequest) => serviceClustersApi.previewPresets(data, tenantId),
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка предпросмотра канонических кластеров');
    },
  });
}

export function useApplyServiceClusterPresets() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: (data: PreviewPresetsRequest) => serviceClustersApi.applyPresets(data, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['medical-services'] });
      queryClient.invalidateQueries({ queryKey: ['service-cluster-presets'] });
      showSuccess('Канонические кластеры применены');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка применения канонических кластеров');
    },
  });
}

export function useSyncServiceClusters() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { tenantId } = useTenantScope();

  return useMutation({
    mutationFn: (data?: { limit?: number; full_sync?: boolean }) =>
      serviceClustersApi.sync(data || { full_sync: true }, tenantId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['service-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['cluster-services'] });
      const result = response.result;
      showSuccess(
        `Синк кластеров завершён: создано ${result.created}, обновлено ${result.updated}, пропущено ${result.skipped}`
      );
    },
    onError: (error: any) => {
      showError(error?.response?.data?.detail || error?.response?.data?.error || 'Ошибка синхронизации кластеров');
    },
  });
}
